import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import SimulatedBadge from "@/components/paper/SimulatedBadge";
import useIsNarrow from "@/hooks/useIsNarrow";

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const STATUS_CLS = {
  FILLED:    "txt-profit",
  OPEN:      "text-amber-400",
  PLACED:    "text-amber-400",
  CANCELLED: "txt-muted",
  REJECTED:  "txt-loss",
};

// RU-TICKFIX TF-6: "MKT" only for a MARKET order; an order whose type is not
// known (e.g. a rejected intent row) shows "—", never "MKT".
export const orderPriceLabel = (o) => {
  if (o?.price) return fmt(o.price);
  return String(o?.order_type || "").toUpperCase() === "MARKET" ? "MKT" : "—";
};

// RU-LIVEORDERSVIEW LO-1: stored times are UTC ISO strings; one without an offset is UTC. Shown in IST:
// "HH:MM:SS" on today's IST day, else "DD Mon HH:MM:SS"; the full IST date-time is the tooltip.
const IST = "Asia/Kolkata";
const HAS_TZ = /(Z|[+-]\d{2}:?\d{2})$/i;

function parseUtc(value) {
  if (!value) return null;
  const s = String(value).trim();
  const ms = Date.parse(HAS_TZ.test(s) ? s : `${s}Z`);
  return isNaN(ms) ? null : ms;
}

const istDay = (ms) => new Intl.DateTimeFormat("en-CA", { timeZone: IST, year: "numeric", month: "2-digit", day: "2-digit" }).format(ms);

export function istTimeLabel(value, nowMs = Date.now()) {
  const ms = parseUtc(value);
  if (ms === null) return { label: "—", title: undefined };
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(ms).map((p) => [p.type, p.value]));
  const time = `${parts.hour}:${parts.minute}:${parts.second}`;
  const date = `${parts.day} ${parts.month}`;
  return {
    label: istDay(ms) === istDay(nowMs) ? time : `${date} ${time}`,
    title: `${date} ${parts.year} ${time} IST`,
  };
}

const MARK_TITLE = {
  broker_ltp: "Unrealised: (broker last price − fill) × open qty",
  contract_tick: "Unrealised: (contract live tick − fill) × open qty",
  broker_pnl: "Unrealised P&L as reported by the broker for this position",
};

// RU-LIVEORDERSVIEW LO-12: the P&L the backend computed (`view`); no P&L math here.
export function livePnl(o) {
  const v = o?.view;
  if (!v) {
    const pnl = o?.pnl ?? o?.realized_pnl;
    return { pnl: pnl ?? null, pct: o?.pnl != null ? o.pnl_pct : o?.realized_pnl_pct ?? null, title: undefined, kind: null };
  }
  const note = v.pnl_note ? ` (${v.pnl_note})` : "";
  if (v.pnl_kind === "realised") {
    return {
      pnl: v.realized_pnl, pct: v.realized_pnl_pct, kind: "realised",
      title: `Booked: realised vs the entry fill (FIFO)${note}`,
    };
  }
  if (v.pnl_kind === "unrealised") {
    const age = v.mark_age_s != null ? `, mark ${Math.round(v.mark_age_s)}s old` : "";
    const mark = v.mark != null ? ` at ${fmt(v.mark)}` : "";
    return {
      pnl: v.unrealized_pnl, pct: v.unrealized_pnl_pct, kind: "unrealised",
      title: `${MARK_TITLE[v.mark_source] || "Unrealised"}${mark}${age}${note}`,
    };
  }
  if (v.position_state === "closed") return { pnl: null, pct: null, kind: null, title: "Closed: P&L is on the exit row" };
  return { pnl: null, pct: null, kind: null, title: v.pnl_note || undefined };
}

// LO-12: stop / target as the backend gave them; the trailing stop shown when it is tighter.
export function liveLevels(o) {
  const v = o?.view;
  // A1 (e): a linked bracket whose levels are null still shows its status.
  if (!v || (v.stop_loss == null && v.target_price == null && !v.bracket_status)) return null;
  const trail = v.trail_stop_effective != null && v.trail_stop_effective !== v.stop_loss ? v.trail_stop_effective : null;
  return {
    sl: trail ?? v.stop_loss,
    trail: trail !== null,
    slTitle: trail !== null ? `Stop ${fmt(v.stop_loss)}; trailing stop ${fmt(trail)} (${v.trail_percent}%)` : undefined,
    tg: v.target_price,
    basis: v.level_basis_label,
    status: v.bracket_status || (v.levels_source === "runner" ? "RUNNER" : null),
  };
}

const BRACKET_STATUS_CLS = {
  ACTIVE: "border-blue-500/40 text-blue-400",
  WAITING: "border-amber-500/40 text-amber-400",
  NEEDS_ATTENTION: "border-red-500/60 text-red-300",
  TARGET_HIT: "border-emerald-500/40 txt-profit",
  STOPPED: "border-red-500/40 txt-loss",
};

function StatusChip({ status, testid }) {
  if (!status) return null;
  return (
    <span data-testid={testid}
          className={`inline-block text-[9px] font-section px-1 border ${BRACKET_STATUS_CLS[status] || "border-[var(--border)] txt-muted"}`}>
      {String(status).replace(/_/g, " ")}
    </span>
  );
}

function LevelCell({ value, trail, title, basis, status, id, which }) {
  if (value == null) {
    return (
      <div className="flex flex-col items-end gap-0.5">
        <span className="txt-muted" data-testid={`live-${which}-${id}`}>—</span>
        {status ? <StatusChip status={status} testid={`live-${which}-status-${id}`} /> : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-end gap-0.5" title={title}>
      <span data-testid={`live-${which}-${id}`}>
        {fmt(value)}{trail ? <span className="ml-1 text-[9px] txt-muted">trail</span> : null}
      </span>
      {basis ? <span className="text-[9px] txt-muted" data-testid={`live-${which}-basis-${id}`}>{basis}</span> : null}
      {status ? <StatusChip status={status} testid={`live-${which}-status-${id}`} /> : null}
    </div>
  );
}

// An intent row (never placed, e.g. a rejected one) has no `id`: its intent id names it.
const rowId = (o) => o.id ?? o.intent_id ?? o.broker_order_id;
const reasonText = (o) => (o.reason ? String(o.reason).replace("_", " ") : (o.intent || "—"));
const reasonCls = (o) => (
  ["stop", "trail", "bracket_stop"].includes(o.reason) ? "txt-loss" :
  ["t1", "t2", "bracket_target"].includes(o.reason) ? "txt-profit" : "txt-muted"
);
const pnlClass = (pnl) => (pnl == null ? "" : pnl > 0 ? "txt-profit" : pnl < 0 ? "txt-loss" : "txt-muted");
const pnlText = (pnl) => (pnl != null ? `₹${fmt(pnl)}` : "—");
const pctText = (pct) => (pct != null ? `${Number(pct).toFixed(2)}%` : "—");

function LiveCards({ orders, nowMs }) {
  return (
    <div className="space-y-2" data-testid="live-orders-cards">
      {orders.length === 0 && <div className="text-center txt-muted py-6">No orders yet.</div>}
      {orders.map((o) => {
        const t = istTimeLabel(o.created_at || o.placed_at, nowMs);
        const p = livePnl(o);
        const lv = liveLevels(o);
        const closedBy = o.view?.closed_by;
        const id = rowId(o);
        return (
          <div key={id} data-testid={`live-order-card-${id}`} className="border border-[var(--border)] p-2 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-section text-sm">{o.symbol}</span>
              <span className={`font-section text-xs px-1.5 py-0.5 ${o.side === "BUY" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{o.side}</span>
              <span className={`font-mono-data text-xs ${STATUS_CLS[o.status?.toUpperCase()] || "txt-muted"}`}>{o.status}</span>
              <span className="ml-auto font-mono-data text-[11px] txt-muted" title={t.title}
                    data-testid={`live-order-card-time-${id}`}>{t.label}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 font-mono-data text-xs">
              <div><div className="overline text-[10px] txt-muted">Qty</div>{o.qty}</div>
              <div><div className="overline text-[10px] txt-muted">Fill ₹</div>{o.avg_fill_price ? fmt(o.avg_fill_price) : "—"}</div>
              <div><div className="overline text-[10px] txt-muted">Order ₹</div>{orderPriceLabel(o)}</div>
              <div title={lv?.slTitle}>
                <div className="overline text-[10px] txt-muted">SL</div>
                <span data-testid={`live-card-sl-${id}`}>{lv?.sl != null ? fmt(lv.sl) : "—"}</span>
                {lv?.trail ? <span className="ml-1 text-[9px] txt-muted">trail</span> : null}
              </div>
              <div>
                <div className="overline text-[10px] txt-muted">TG</div>
                <span data-testid={`live-card-tg-${id}`}>{lv?.tg != null ? fmt(lv.tg) : "—"}</span>
              </div>
              <div className={pnlClass(p.pnl)} title={p.title} data-testid={`live-card-pnl-${id}`}>
                <div className="overline text-[10px] txt-muted">P&L{p.kind === "realised" ? " (booked)" : ""}</div>
                {pnlText(p.pnl)} <span className="text-[10px]">{pctText(p.pct)}</span>
              </div>
            </div>
            {(lv?.basis || lv?.status || closedBy || o.reason || o.intent) ? (
              <div className="flex items-center gap-2 flex-wrap text-[10px]">
                {lv?.basis ? <span className="txt-muted">levels {lv.basis}</span> : null}
                <StatusChip status={lv?.status} testid={`live-card-status-${id}`} />
                <span className={`font-mono-data ${reasonCls(o)}`}>{reasonText(o)}</span>
                {closedBy ? <StatusChip status={closedBy.status} testid={`live-card-closedby-${id}`} /> : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default function OrdersTable({ orders, mode = "paper", nowMs }) {
  const isLive = mode === "live";
  const narrow = useIsNarrow();
  const now = nowMs ?? Date.now();
  return (
    <div className="panel p-3 sm:p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="overline">Recent orders</div>
        <span className={`text-[10px] font-section px-2 py-0.5 border ${isLive ? "border-red-500/40 text-red-300 bg-red-500/10" : "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"}`}>
          {isLive ? "LIVE" : "PAPER"}
        </span>
        <span className="ml-auto text-[10px] txt-muted">times IST</span>
      </div>
      {isLive && narrow ? (
        <div className="max-h-[32rem] overflow-auto"><LiveCards orders={orders} nowMs={now} /></div>
      ) : (
      <div className="max-h-72 overflow-auto overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--border)]">
              <TableHead className="overline">Time</TableHead>
              <TableHead className="overline">Symbol</TableHead>
              <TableHead className="overline">Side</TableHead>
              <TableHead className="overline text-right">Qty</TableHead>
              <TableHead className="overline text-right">Order ₹</TableHead>
              <TableHead className="overline text-right">Fill ₹</TableHead>
              {isLive && <TableHead className="overline text-right">SL</TableHead>}
              {isLive && <TableHead className="overline text-right">TG</TableHead>}
              <TableHead className="overline text-right">P&L</TableHead>
              <TableHead className="overline text-right">P&L %</TableHead>
              <TableHead className="overline">Status</TableHead>
              <TableHead className="overline">Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && (
              <TableRow className="border-[var(--border)]">
                <TableCell colSpan={isLive ? 12 : 10} className="text-center txt-muted py-6">No orders yet.</TableCell>
              </TableRow>
            )}
            {orders.map((o) => {
              const p = isLive ? livePnl(o) : {
                pnl: o.pnl ?? o.realized_pnl, pct: o.pnl != null ? o.pnl_pct : o.realized_pnl_pct, title: undefined,
              };
              const pnlCls = pnlClass(p.pnl);
              const statusCls = STATUS_CLS[o.status?.toUpperCase()] || "txt-muted";
              const t = istTimeLabel(o.created_at || o.placed_at, now);
              const lv = isLive ? liveLevels(o) : null;
              const closedBy = isLive ? o.view?.closed_by : null;
              const id = rowId(o);
              return (
                <TableRow key={id} className="border-[var(--border)]">
                  <TableCell className="font-mono-data text-xs whitespace-nowrap" title={t.title}
                             data-testid={`order-time-${id}`}>{t.label}</TableCell>
                  <TableCell className="font-section text-sm">
                    {o.symbol}
                    {o.simulated ? <span className="ml-1.5"><SimulatedBadge testid={`order-simulated-${id}`} /></span> : null}
                  </TableCell>
                  <TableCell>
                    <span className={`font-section text-xs px-1.5 py-0.5 ${o.side === "BUY" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                      {o.side}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono-data text-right">{o.qty}</TableCell>
                  <TableCell className="font-mono-data text-right text-xs txt-secondary">{orderPriceLabel(o)}</TableCell>
                  <TableCell className="font-mono-data text-right">{o.avg_fill_price ? fmt(o.avg_fill_price) : "—"}</TableCell>
                  {isLive && (
                    <TableCell className="font-mono-data text-right text-xs">
                      <LevelCell value={lv?.sl} trail={lv?.trail} title={lv?.slTitle} basis={lv?.basis}
                                 status={lv?.status} id={id} which="sl" />
                    </TableCell>
                  )}
                  {isLive && (
                    <TableCell className="font-mono-data text-right text-xs">
                      <LevelCell value={lv?.tg} basis={lv?.basis} id={id} which="tg" />
                    </TableCell>
                  )}
                  <TableCell className={`font-mono-data text-right ${pnlCls}`} title={p.title}
                             data-testid={`order-pnl-${id}`}>
                    {pnlText(p.pnl)}
                    {p.kind === "realised" ? <div className="text-[9px] txt-muted">booked</div> : null}
                  </TableCell>
                  <TableCell className={`font-mono-data text-right text-xs ${pnlCls}`} title={p.title}
                             data-testid={`order-pnl-pct-${id}`}>
                    {/* Realised P&L over the capital at entry, lot-aware, from the backend (RU-PNL G-36). */}
                    {pctText(p.pct)}
                  </TableCell>
                  <TableCell className={`font-mono-data text-xs ${statusCls}`}>{o.status}</TableCell>
                  <TableCell className={`font-mono-data text-xs ${reasonCls(o)}`} title={o.intent ? `intent: ${o.intent}` : undefined}>
                    {reasonText(o)}
                    {closedBy ? <span className="ml-1"><StatusChip status={closedBy.status} testid={`order-closedby-${id}`} /></span> : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}
    </div>
  );
}
